import * as React from "react";
import clsx from "clsx";
import { App, Button } from "antd";
import { StepIndicator } from "@/components/ui/StepIndicator";
import { DialogMessageType } from "@/types/commands";
import Ring from "@/components/ui/Ring";
import { useContentSlideStore } from "@/state/contentSlideStore";
import {
  createChartConfig,
  NewPrecedentChartType,
  useNewPrecedentSlideStore,
} from "@/state/newPrecedentSlideStore";
import { Step } from "../components/Step";
import SlideLayoutStep from "./steps/SlideLayoutStep";
import { ChartTypeSelector } from "@/Popups/contentSlide/newPrecedentSlide/steps/ChartTypeSelector";
import FormattingFeaturesStep from "./steps/FormattingFeaturesStep";
import {
  isSlotChartFeaturesComplete,
  isWizardFormattingFeaturesComplete,
} from "./steps/formattingFeaturesConfig";
import FormattingSelectionStep from "./steps/FormattingSelectionStep";
import TimePeriodBenchmarkStep from "./steps/TimePeriodBenchmarkStep";

const NEW_PRECEDENT_STEP_LABELS = [
  "Slide Layout",
  "Chart Type",
  "Customization Options",
  "Time Period and Relative Benchmark(s)",
  "Formatting",
];

const STEP_SIZES = [0.8, 0.8, 1, 1.75, 0.85];

/** Chart type + formatting steps: one sub-question per chart when the layout has two charts. */
function perChartWizardQuestionCount(chartCount: 1 | 2 | null): number {
  return chartCount === 2 ? 2 : 1;
}

function wizardQuestionsPerStep(chartCount: 1 | 2 | null): [number, number, number, number, number] {
  const n = perChartWizardQuestionCount(chartCount);
  return [1, n, n, 1, 1];
}

export default function NewPrecedentSlide() {
  const { modal } = App.useApp();

  const setPrecedentPanelMode = useContentSlideStore((s) => s.setPrecedentPanelMode);
  const setCreateNewPrecedentPending = useContentSlideStore((s) => s.setCreateNewPrecedentPending);

  const step = useNewPrecedentSlideStore((s) => s.step);
  const answersByStep = useNewPrecedentSlideStore((s) => s.answersByStep);
  const selectedLayoutId = useNewPrecedentSlideStore((s) => s.selectedLayoutId);
  const chartCount = useNewPrecedentSlideStore((s) => s.chartCount);
  const setChartCount = useNewPrecedentSlideStore((s) => s.setChartCount);
  const chartConfig = useNewPrecedentSlideStore((s) => s.chartConfig);
  const isKeyEvent = useNewPrecedentSlideStore((s) => s.isKeyEvent);
  const setChartSlotType = useNewPrecedentSlideStore((s) => s.setChartSlotType);
  const sharePriceType = useNewPrecedentSlideStore((s) => s.sharePriceType);
  const sharePriceCurrency = useNewPrecedentSlideStore((s) => s.sharePriceCurrency);
  const reset = useNewPrecedentSlideStore((s) => s.reset);
  const selectedFormatTemplateId = useNewPrecedentSlideStore((s) => s.selectedFormatTemplateId);

  React.useEffect(() => {
    reset();
  }, [reset]);

  React.useEffect(() => {
    if (!selectedLayoutId) {
      setChartCount(null);
      useNewPrecedentSlideStore.setState({
        chartConfig: createChartConfig(1),
      });
      return;
    }

    // Per your mapping:
    // - 101/201 => 1 chart
    // - 111/211 => 2 charts
    const nextCount = selectedLayoutId === "layout_111" || selectedLayoutId === "layout_211" ? 2 : 1;
    setChartCount(nextCount);
    useNewPrecedentSlideStore.setState({
      chartConfig: createChartConfig(nextCount),
    });
  }, [selectedLayoutId, setChartCount]);

  const sharePriceCharacteristicsComplete = Boolean(sharePriceType && sharePriceCurrency);
  const formattingComplete =
    isWizardFormattingFeaturesComplete(
      chartCount,
      chartConfig.chart1.features,
      chartConfig.chart2?.features,
      isKeyEvent
    ) && sharePriceCharacteristicsComplete;

  const chart1FormattingSlotComplete = isSlotChartFeaturesComplete(
    chartConfig.chart1.features,
    isKeyEvent
  );
  const chart2FormattingSlotComplete =
    chartCount === 2 && chartConfig.chart2 !== undefined
      ? isSlotChartFeaturesComplete(chartConfig.chart2.features, isKeyEvent)
      : false;

  const questionsPerStep = React.useMemo(
    () => wizardQuestionsPerStep(chartCount),
    [chartCount]
  );

  React.useEffect(() => {
    const chartTypesComplete =
      chartCount === 1
        ? !!chartConfig.chart1.chartType
        : chartCount === 2
          ? !!chartConfig.chart1.chartType && !!chartConfig.chart2?.chartType
          : false;

    const chartTypeStepAnswers =
      chartCount === 2
        ? (!!chartConfig.chart1.chartType ? 1 : 0) + (!!chartConfig.chart2?.chartType ? 1 : 0)
        : chartTypesComplete
          ? 1
          : 0;

    const formattingStepAnswers =
      chartCount === 2
        ? (chart1FormattingSlotComplete ? 1 : 0) + (chart2FormattingSlotComplete ? 1 : 0)
        : formattingComplete
          ? 1
          : 0;

    useNewPrecedentSlideStore.setState((s) => ({
      answersByStep: [
        selectedLayoutId ? 1 : 0,
        chartTypeStepAnswers,
        formattingStepAnswers,
        s.answersByStep[3],
        s.answersByStep[4],
      ],
    }));
  }, [
    selectedLayoutId,
    chartCount,
    chartConfig.chart1.chartType,
    chartConfig.chart2?.chartType,
    chart1FormattingSlotComplete,
    chart2FormattingSlotComplete,
    formattingComplete,
    sharePriceType,
    sharePriceCurrency,
  ]);

  React.useEffect(() => {
    const qps = wizardQuestionsPerStep(chartCount);
    const filled = selectedFormatTemplateId ? qps[4] : 0;
    useNewPrecedentSlideStore.setState((s) => {
      if (s.answersByStep[4] === filled) return s;
      const next = [...s.answersByStep] as [number, number, number, number, number];
      next[4] = filled;
      return { answersByStep: next };
    });
  }, [selectedFormatTemplateId, chartCount]);

  const chartTypesComplete =
    chartCount === 1
      ? !!chartConfig.chart1.chartType
      : chartCount === 2
        ? !!chartConfig.chart1.chartType && !!chartConfig.chart2?.chartType
        : false;

  const onClose = React.useCallback(() => {
    try {
      Office.context.ui.messageParent(DialogMessageType.CLOSE);
    } catch {}
  }, []);

  const goBack = React.useCallback(() => {
    if (step <= 0) {
      setPrecedentPanelMode("grid");
      setCreateNewPrecedentPending(true);
      return;
    }
    useNewPrecedentSlideStore.setState({ step: step - 1 });
  }, [step, setPrecedentPanelMode, setCreateNewPrecedentPending]);

  const goNext = React.useCallback(() => {
    if (step === 0 && !selectedLayoutId) return;
    if (step === 1 && !chartTypesComplete) return;
    if (step === 2 && !formattingComplete) return;
    if (step === 4 && !selectedFormatTemplateId) return;

    if (step === 1) {
      const { chartConfig, chartCount: count } = useNewPrecedentSlideStore.getState();
      const chart1Column = chartConfig.chart1.chartType === "columnChart";
      const chart2Column = count === 2 && chartConfig.chart2?.chartType === "columnChart";
      if (chart1Column || chart2Column) {
        modal.error({ content: "Column Chart is not yet integrated." });
        return;
      }
    }

    if (step >= NEW_PRECEDENT_STEP_LABELS.length - 1) {
      onClose();
      return;
    }

    useNewPrecedentSlideStore.setState((st) => {
      const next = [...st.answersByStep] as [number, number, number, number, number];
      const qps = wizardQuestionsPerStep(st.chartCount);
      next[st.step] = qps[st.step];
      return { answersByStep: next, step: st.step + 1 };
    });
  }, [
    step,
    selectedLayoutId,
    chartTypesComplete,
    formattingComplete,
    sharePriceCharacteristicsComplete,
    onClose,
    selectedFormatTemplateId,
    modal,
  ]);

  const isLastStep = step === NEW_PRECEDENT_STEP_LABELS.length - 1;

  return (
    <div className="flex flex-col justify-between h-full min-h-screen grid-rows-[auto_1fr_auto] gap-x-2 px-4 py-4 bg-gray-100">
      <div
        className={clsx(
          "w-full mx-auto py-4 shadow-md px-4 rounded-sm z-10 border border-gray-200 border-b-[2px] border-b-[#ff9336] bg-white"
        )}
      >
        <StepIndicator
          currentStep={step}
          answersByStep={answersByStep}
          questionsPerStep={questionsPerStep}
          labels={[...NEW_PRECEDENT_STEP_LABELS]}
          stepSizes={STEP_SIZES}
          labelClassName="text-xs leading-snug"
        />
      </div>

      <Ring className="grid h-full relative w-full mt-4 !border !border-gray-200">
        <Step show={step === 0}>
          <SlideLayoutStep />
        </Step>

        <Step show={step === 1}>
            <ChartTypeSelector
              chartCount={chartCount}
              value={[chartConfig.chart1.chartType, chartConfig.chart2?.chartType]}
              onChange={(idx, type) => setChartSlotType(idx, type as NewPrecedentChartType)}
            />
        </Step>

        <Step show={step === 2}>
          <FormattingFeaturesStep />
        </Step>

        <Step show={step === 3}>
         <TimePeriodBenchmarkStep />
        </Step>

        <Step show={step === 4}>
          <FormattingSelectionStep/>
        </Step>
      </Ring>

      <div className={`flex items-center w-full px-2 pl-10 pt-3 justify-end`}>
        <div className="flex gap-2">
          <Button danger onClick={onClose}>
            Cancel
          </Button>
          <Button type="default" onClick={goBack}>
            Back
          </Button>
          <Button
            type="primary"
            onClick={goNext}
            disabled={
              (step === 0 && !selectedLayoutId) ||
              (step === 1 && !chartTypesComplete) ||
              (step === 2 && !formattingComplete) ||
              (step === 4 && !selectedFormatTemplateId)
            }
          >
            {isLastStep ? "Finish" : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
}

